import { ReactNode, useEffect } from "react"
import { Linking } from "react-native"
import { useNavigationContext } from "../hooks"
import { navigate } from "../services/NavigationService.ts"
import { shouldAllowWebPaymentRequest } from "../utils"

function getPaymentRequestDeepLinkParams(deepLink: string) {
  const [path, queryString] = deepLink.split("?")
  const parts = path.split("/")
  const searchParams = new URLSearchParams(queryString || "")

  return {
    paymentRequestId: parts[parts.length - 1],
    returnUrl: searchParams.get("returnUrl") ?? undefined,
  }
}

// Handle log in flow when a specific screen
// is opened via a deep link
export function DeepLinkHandler({ children }: { children: ReactNode }) {
  const {
    setDeepLink,
    isLoggedIn,
    deepLink,
    setDeepLinkHandled,
    deepLinkHandled,
    getDeepLinkHandled,
    initLinkHandled,
    setInitLinkHandled,
  } = useNavigationContext()

  useEffect(() => {
    if (isLoggedIn) {
      // React Navigation handles this case already
      return
    }

    Linking.getInitialURL().then((initUrl) => {
      if (!initUrl || initUrl === deepLink || initLinkHandled) {
        // React Navigation handles this case already
        return
      }
      // only handle links that need being logged in
      // simply open the app in other cases
      if (
        initUrl.toLowerCase().includes("/paymentrequest/") ||
        initUrl.toLowerCase().includes("/debitauth/") ||
        initUrl.toLowerCase().includes("/recurringpayment/")
      ) {
        if (initUrl.toLowerCase().includes("/paymentrequest/") && !shouldAllowWebPaymentRequest()) {
          navigate("NotSupportedOnWeb", {})
        }
      }
      setDeepLink(initUrl)
      setDeepLinkHandled(false)
      setInitLinkHandled(true)
    })
  }, [deepLink, initLinkHandled, isLoggedIn, setDeepLink, setDeepLinkHandled, setInitLinkHandled])

  useEffect(() => {
    const subscription = Linking.addEventListener("url", (event) => {
      // React Navigation handles this case already
      if (isLoggedIn) {
        return
      }
      // only handle links that need being logged in
      // simply open the app in other cases
      if (event.url.toLowerCase().includes("/paymentrequest/")) {
        if (!shouldAllowWebPaymentRequest()) {
          navigate("NotSupportedOnWeb", {})
          return
        }
        navigate("LogIn", {})
      }
      if (event.url.toLowerCase().includes("/recurringpayment/")) {
        navigate("LogIn", {})
      }
      setDeepLink(event.url)
      setDeepLinkHandled(false)
    })

    return () => {
      subscription.remove()
    }
  }, [deepLink, isLoggedIn, setDeepLink, setDeepLinkHandled])

  useEffect(() => {
    if (!isLoggedIn || !deepLink || deepLinkHandled) {
      return
    }
    if (deepLink.toLowerCase().includes("/paymentrequest/")) {
      if (!shouldAllowWebPaymentRequest()) {
        navigate("NotSupportedOnWeb", {})
        return
      }
      const timeout = setTimeout(() => {
        // Login can mark the link handled before this timer runs, and that
        // update has not re-rendered yet.
        if (getDeepLinkHandled()) {
          return
        }
        navigate("PaymentMethod", getPaymentRequestDeepLinkParams(deepLink))
        setDeepLinkHandled(true)
      })
      return () => clearTimeout(timeout)
    }
    if (deepLink.toLowerCase().includes("/recurringpayment/")) {
      const timeout = setTimeout(() => {
        if (getDeepLinkHandled()) {
          return
        }
        const parts = deepLink.split("/")
        const planConsentId = parts[parts.length - 1]
        navigate("PlanConsentRequest", { planConsentId })
        setDeepLinkHandled(true)
      })
      return () => clearTimeout(timeout)
    }
  }, [isLoggedIn, deepLink, getDeepLinkHandled, setDeepLink, setDeepLinkHandled, deepLinkHandled])

  return <>{children}</>
}
